import { memo } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';
import Loadable from 'react-loadable';

import { AtlassianNavigation } from '@atlaskit/atlassian-navigation';

import { isChildrenMenuItems } from '../../types';

import { HorizontalAppSwitcher } from './horizontal-app-switcher';
import { HorizontalCreate } from './horizontal-create';
import { HorizontalHelpPopup } from './horizontal-help-popup';
import {
  HorizontalNavProps,
  HorizontalNavContext,
} from './horizontal-nav-context';
import MoreMenuDropdown from './horizontal-nav-more-menu';
import PrimaryDropdownItem from './horizontal-nav-primary-dropdown-item';
import { HorizontalNavPrimaryItem } from './horizontal-nav-primary-item';
import messages from './horizontal-nav.i18n';
import HorizontalProfilePopup from './horizontal-profile-popup';
import HorizontalSettings from './horizontal-settings';
import { ProductHomeWithPride } from './product-home';

const HorizontalPlatformSearch = Loadable({
  loading: () => null,
  loader: () => import('./horizontal-platform-search'),
});

// only show platform notifications if the user is logged in
const PlatformNotifications = Loadable({
  loading: () => null,
  loader: () => import('./platform-notifications'),
});

const HorizontalNav = (
  props: HorizontalNavProps & {
    hasPride: boolean;
    isCurrentUserAnonymous: boolean;
    intl: InjectedIntl;
    tenantId?: string;
  }
) => {
  const { intl, hasPride, tenantId, isCurrentUserAnonymous, ...otherProps } =
    props;

  return (
    // Need to provide props to HorizontalCreate / HorizontalSearch / HorizontalProfilePopup
    // using context instead of directly applying props, because the AtlassianNavigation
    // does not pass props down by design.

    <HorizontalNavContext.Provider value={otherProps}>
      <AtlassianNavigation
        label={intl.formatMessage(messages.navigationLabel)}
        renderProductHome={() => (
          <ProductHomeWithPride
            hasPride={hasPride}
            handleProductClick={props.handleProductClick}
          />
        )}
        primaryItems={[
          ...props.menuItems.mainItems.map(menuItem => {
            if (
              menuItem.is_dropdown_item &&
              menuItem.children &&
              isChildrenMenuItems(menuItem.children)
            ) {
              return (
                <PrimaryDropdownItem
                  key={menuItem.label}
                  menuItems={menuItem.children}
                  linkComponent={props.linkComponent}
                  analyticsLabel={menuItem.analytics_label}
                  label={menuItem.label}
                  publishUiEvent={props.publishUiEvent}
                >
                  {/* here must be a component that fetches and renders resent items */}
                </PrimaryDropdownItem>
              );
            }
            return (
              <HorizontalNavPrimaryItem
                analyticsLabel={menuItem.analytics_label}
                publishUiEvent={props.publishUiEvent}
                linkComponent={props.linkComponent}
                key={menuItem.url}
                href={menuItem.url}
                isClientLink={menuItem.is_client_link}
                target={menuItem.target}
                isHighlighted={menuItem.url === window.location.pathname}
              >
                {menuItem.label}
              </HorizontalNavPrimaryItem>
            );
          }),
          <MoreMenuDropdown
            key="moreMenu"
            menuItems={props.menuItems.secondaryItems}
            linkComponent={props.linkComponent}
            publishUiEvent={props.publishUiEvent}
          />,
        ]}
        renderAppSwitcher={() => (
          <HorizontalAppSwitcher
            tenantId={tenantId}
            isBeingRenderedInsideMobileNav={false}
            publishUiEvent={props.publishUiEvent}
          />
        )}
        renderCreate={HorizontalCreate}
        renderHelp={HorizontalHelpPopup}
        renderSearch={HorizontalPlatformSearch}
        renderProfile={HorizontalProfilePopup}
        renderSettings={otherProps.isLoggedIn ? HorizontalSettings : undefined}
        renderNotifications={
          isCurrentUserAnonymous ? undefined : PlatformNotifications
        }
      />
    </HorizontalNavContext.Provider>
  );
};

export default memo(injectIntl(HorizontalNav));
